import { traceHttp } from '../lib/log';
import { adoptStoredTokens, getSession, jwtExpiry, sessionStore, type AuthSession } from './session';
import { resolveServerPath, withQuery } from './url';

export class AbsError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** No HTTP answer: offline, unreachable, or timed out (`isTimeout`). Never a reason to sign out. */
    readonly isNetworkError = false,
    /** The server did not answer in time: it is reachable but slow ("taking too long" copy, not "no connection"). */
    readonly isTimeout = false,
  ) {
    super(message);
    this.name = 'AbsError';
  }
  get isUnauthorized() {
    return this.status === 401;
  }
}

export const TIMEOUT_MESSAGE = 'The server took too long to respond.';
export const UNREACHABLE_MESSAGE = 'Could not reach the server. Check your connection.';

function timeoutError(): AbsError {
  return new AbsError(TIMEOUT_MESSAGE, 0, true, true);
}

const isTimeoutLike = (err: unknown): boolean => {
  const name = (err as Error)?.name;
  return name === 'TimeoutError' || name === 'AbortError';
};

/** Default timeout of API requests (the active connection's timeout only ever raises it). */
export const DEFAULT_REQUEST_TIMEOUT_MS = 20_000;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Milliseconds before the request is aborted. Defaults to the request defaults (20 s). */
  timeoutMs?: number;
  /** Skip the bearer header (login, status). */
  anonymous?: boolean;
  headers?: Record<string, string>;
  /** Override the session (used during login before the session is stored). */
  session?: Pick<AuthSession, 'serverUrl' | 'accessToken' | 'refreshToken'> & { headers?: Record<string, string> };
  /** Return the raw Response instead of parsed JSON. */
  raw?: boolean;
}

type Identity = Pick<AuthSession, 'serverUrl' | 'userId'>;

/** `serverUrl|userId`: the account a session belongs to (tokens rotate, the account does not). */
export function sessionIdentity(session: Identity | null | undefined): string | null {
  return session ? `${session.serverUrl}|${session.userId}` : null;
}

/** Rejection of a request whose account was signed out or switched while it ran. */
export function accountChangedError(): DOMException {
  return new DOMException('The account changed while this request was running.', 'AbortError');
}

type UnauthorizedListener = (session: AuthSession) => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/**
 * Fires when the server rejects the active session's credentials and they
 * cannot be refreshed. Never fires for a request whose account is no longer
 * the active one.
 */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

export interface RefreshedTokens {
  serverUrl: string;
  userId: string;
  accessToken: string;
  refreshToken?: string;
}

type TokensListener = (tokens: RefreshedTokens) => void;
const detachedTokenListeners = new Set<TokensListener>();

/**
 * Fires when a refresh finished after its account stopped being the active
 * one. The server already rotated the refresh token, so the owner of saved
 * credentials (connections) must store the new pair.
 */
export function onDetachedTokenRefresh(listener: TokensListener): () => void {
  detachedTokenListeners.add(listener);
  return () => detachedTokenListeners.delete(listener);
}

/**
 * `ok`: new token stored. `rejected`: the server refused (sign in again).
 * `failed`: couldn't tell (offline, timeout, 5xx), with the HTTP status when
 * there was one. `stale`: the account changed while refreshing.
 */
type RefreshResult = { status: 'ok' | 'rejected' | 'stale' } | ({ status: 'failed' } & RefreshFailure);

interface RefreshFailure {
  /** HTTP status of a failed refresh (5xx, unexpected codes); absent when there was no answer. */
  httpStatus?: number;
  timedOut?: boolean;
}

const OK: RefreshResult = { status: 'ok' };
const REJECTED: RefreshResult = { status: 'rejected' };
const STALE: RefreshResult = { status: 'stale' };

/** One refresh per account at a time; requests of the same account join it. */
const refreshInFlight = new Map<string, Promise<RefreshResult>>();

/**
 * Exchanges the active session's refresh token for a new access token
 * (ABS 2.26+). Concurrent callers share a single request.
 */
export async function refreshAccessToken(): Promise<boolean> {
  return (await refreshSession(getSession())).status === 'ok';
}

/** Expiry (ms since epoch) of a JWT access token, or null for tokens that never expire or aren't JWTs. */
export function tokenExpiresAt(token: string | undefined): number | null {
  return jwtExpiry(token);
}

/**
 * Refreshes the access token when it expires within `marginMs`. Media URLs
 * carry the token, so a stream opened with a token that is about to expire
 * fails on its next range request.
 */
export async function ensureFreshToken(marginMs = 120_000): Promise<void> {
  const s = getSession();
  if (!s?.refreshToken) return;
  const exp = tokenExpiresAt(s.accessToken);
  if (exp != null && exp - Date.now() < marginMs) await refreshSession(s);
}

export type TokenRefresh =
  | { status: 'ok'; accessToken: string; refreshToken?: string }
  | { status: 'rejected' }
  | ({ status: 'failed' } & RefreshFailure);

/**
 * Exchanges a refresh token for new tokens without touching the active
 * session. Used for the active session and for saved connections that are
 * not active (verifying a connection before switching to it).
 */
export async function refreshTokensFor(session: Pick<AuthSession, 'serverUrl' | 'refreshToken'>, extraHeaders: Record<string, string> = {}): Promise<TokenRefresh> {
  if (!session.refreshToken) return { status: 'rejected' };
  const url = resolveServerPath(session.serverUrl, '/auth/refresh');
  const started = now();
  try {
    const res = await fetchWithTimeout(
      url,
      {
        method: 'POST',
        headers: { 'x-refresh-token': session.refreshToken, 'x-return-tokens': 'true', ...extraHeaders },
        credentials: 'omit',
      },
      15000,
    );
    traceHttp('POST', url, res.status, now() - started);
    if (res.status === 401 || res.status === 403) return { status: 'rejected' };
    if (!res.ok) return { status: 'failed', httpStatus: res.status };
    const data = (await res.json()) as { user?: { accessToken?: string; refreshToken?: string } };
    const accessToken = data.user?.accessToken;
    if (!accessToken) return { status: 'rejected' };
    return { status: 'ok', accessToken, refreshToken: data.user?.refreshToken ?? session.refreshToken };
  } catch (err) {
    const timedOut = isTimeoutLike(err);
    traceHttp('POST', url, timedOut ? 'timeout' : 'network', now() - started);
    return timedOut ? { status: 'failed', timedOut } : { status: 'failed' };
  }
}

/** The error a request rejects with when its token expired and could not be refreshed for a transient reason. */
function refreshFailureError(failure: RefreshFailure): AbsError {
  if (failure.timedOut) return timeoutError();
  if (failure.httpStatus != null) {
    return new AbsError(
      failure.httpStatus >= 500 ? 'The server is temporarily unavailable. Please try again.' : `Request failed (${failure.httpStatus})`,
      failure.httpStatus,
    );
  }
  return new AbsError(UNREACHABLE_MESSAGE, 0, true);
}

/** Refresh tokens rotate (ABS 2.26+), so tabs sharing an account must never spend one twice. */
const REFRESH_LOCK_PREFIX = 'plume-token-refresh:';

/**
 * Runs `fn` while holding a cross-tab lock for the account (Web Locks), so
 * only one tab or window exchanges the refresh token at a time; the others
 * wait and then adopt the rotated tokens (session.ts). Runs `fn` directly
 * where Web Locks are missing.
 */
function withRefreshLock<T>(identity: string, fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;
  if (!locks?.request) return fn();
  return locks.request(REFRESH_LOCK_PREFIX + identity, () => fn()) as Promise<T>;
}

/** The access token outlives the next minute (a token that is not a JWT is assumed valid). */
function usable(token: string | undefined): boolean {
  if (!token) return false;
  const exp = tokenExpiresAt(token);
  return exp == null || exp - Date.now() > 60_000;
}

/**
 * The account's session after picking up tokens another tab stored, when
 * they replace `used` (the pair this tab just tried); null otherwise.
 */
function rotatedElsewhere(identity: string, used: Pick<AuthSession, 'accessToken' | 'refreshToken'>): AuthSession | null {
  adoptStoredTokens();
  const current = getSession();
  if (!current || sessionIdentity(current) !== identity) return null;
  return current.accessToken !== used.accessToken || current.refreshToken !== used.refreshToken ? current : null;
}

/**
 * Refreshes `session`'s tokens. Only the active account is refreshed: the
 * result is stored in `sessionStore` when that account is still active, and
 * handed to `onDetachedTokenRefresh` listeners otherwise.
 *
 * Tabs share the account's tokens through localStorage: the exchange runs
 * under a cross-tab lock, a tab that waited for it first adopts tokens
 * another tab rotated meanwhile, and a refused refresh token is checked
 * against the stored one before it counts as a rejection (browsers without
 * Web Locks), so one tab's rotation never signs the others out.
 */
function refreshSession(session: AuthSession | null): Promise<RefreshResult> {
  const identity = sessionIdentity(session);
  if (!session || !identity || identity !== sessionIdentity(getSession())) return Promise.resolve(STALE);
  if (!session.refreshToken) return Promise.resolve(REJECTED);
  const pending = refreshInFlight.get(identity);
  if (pending) return pending;
  const headers = requestDefaults.headers;
  const run = withRefreshLock(identity, async (): Promise<RefreshResult> => {
    let used: AuthSession = session;
    // Another tab refreshed while this one waited for the lock (or earlier).
    const adopted = rotatedElsewhere(identity, used);
    if (adopted) {
      if (usable(adopted.accessToken)) return OK;
      used = adopted;
    }
    let result = await refreshTokensFor(used, headers);
    if (result.status === 'rejected') {
      // A tab without the lock rotated the token first: retry with the pair it stored.
      const rotated = rotatedElsewhere(identity, used);
      if (rotated?.refreshToken && rotated.refreshToken !== used.refreshToken) {
        if (usable(rotated.accessToken)) return OK;
        used = rotated;
        result = await refreshTokensFor(used, headers);
      }
    }
    if (result.status !== 'ok') return result;
    const current = getSession();
    if (!current || sessionIdentity(current) !== identity) {
      const tokens = { serverUrl: session.serverUrl, userId: session.userId, accessToken: result.accessToken, refreshToken: result.refreshToken };
      detachedTokenListeners.forEach((l) => l(tokens));
      return STALE;
    }
    sessionStore.set({ ...current, accessToken: result.accessToken, refreshToken: result.refreshToken ?? current.refreshToken });
    return OK;
  }).finally(() => {
    if (refreshInFlight.get(identity) === run) refreshInFlight.delete(identity);
  });
  refreshInFlight.set(identity, run);
  return run;
}

interface RequestDefaults {
  /** Default timeout for requests that don't set their own. */
  timeoutMs?: number;
  /** Extra headers sent on API requests of the active session (not on media URLs, which can't carry headers). */
  headers?: Record<string, string>;
}

let requestDefaults: RequestDefaults = {};

/** Applies a connection's advanced options (timeout, custom headers) to API requests. */
export function configureRequestDefaults(defaults: RequestDefaults): void {
  requestDefaults = { ...defaults, headers: { ...defaults.headers } };
}

// Requests made with the active session are aborted as soon as its account
// changes (switch, sign-out), so nothing of the old account lands later.
let boundIdentity = sessionIdentity(getSession());
let accountAbort = new AbortController();
sessionStore.subscribe(() => {
  const next = sessionIdentity(getSession());
  if (next === boundIdentity) return;
  boundIdentity = next;
  accountAbort.abort(accountChangedError());
  accountAbort = new AbortController();
});

const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number, signals: Array<AbortSignal | undefined> = []): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException('Request timed out', 'TimeoutError')), timeoutMs);
  const outer = signals.filter((s): s is AbortSignal => !!s);
  const unlink = outer.map((signal) => {
    const onAbort = () => controller.abort(signal.reason);
    if (signal.aborted) onAbort();
    else signal.addEventListener('abort', onAbort, { once: true });
    return () => signal.removeEventListener('abort', onAbort);
  });
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
    unlink.forEach((u) => u());
  }
}

export async function absRequest<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const active = opts.session ? null : getSession();
  const session = opts.session ?? active;
  if (!session) throw new AbsError('Not signed in', 401);
  // Requests on the active session are bound to its account: aborted when it
  // changes, and never refreshed or retried with another account's tokens.
  const identity = sessionIdentity(active);
  const accountSignal = active ? accountAbort.signal : undefined;
  const currentSession = (): AuthSession | null => {
    const s = getSession();
    return s && sessionIdentity(s) === identity ? s : null;
  };

  const send = async (token: string | undefined) => {
    // A connection's custom headers never go to another server (session overrides).
    const headers: Record<string, string> = { Accept: 'application/json', ...(opts.session ? opts.session.headers : requestDefaults.headers), ...opts.headers };
    if (!opts.anonymous && token) headers.Authorization = `Bearer ${token}`;
    let body: BodyInit | undefined;
    if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
    const url = withQuery(resolveServerPath(session.serverUrl, path), opts.query ?? {});
    const method = opts.method ?? 'GET';
    const started = now();
    try {
      const res = await fetchWithTimeout(
        url,
        { method, headers, body, credentials: 'omit', cache: 'no-store' },
        opts.timeoutMs ?? requestDefaults.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS,
        [opts.signal, accountSignal],
      );
      traceHttp(method, url, res.status, now() - started);
      return res;
    } catch (err) {
      if (opts.signal?.aborted && (err as Error)?.name === 'AbortError') {
        traceHttp(method, url, 'cancelled', now() - started);
        throw err;
      }
      if (accountSignal?.aborted) throw accountChangedError();
      const timedOut = isTimeoutLike(err);
      traceHttp(method, url, timedOut ? 'timeout' : 'network', now() - started);
      throw timedOut ? timeoutError() : new AbsError(UNREACHABLE_MESSAGE, 0, true);
    }
  };

  let res = await send(session.accessToken);

  if (res.status === 401 && !opts.anonymous && active) {
    let latest = currentSession();
    if (!latest) throw accountChangedError();
    // Another request may already have rotated the token.
    if (latest.accessToken !== active.accessToken) res = await send(latest.accessToken);
    if (res.status === 401) {
      const refreshed = await refreshSession(latest);
      // A flaky connection or a struggling server must never sign the user out; retry later.
      if (refreshed.status === 'failed') throw refreshFailureError(refreshed);
      latest = currentSession();
      if (refreshed.status === 'stale' || !latest) throw accountChangedError();
      if (refreshed.status === 'ok') res = await send(latest.accessToken);
    }
    if (res.status === 401) {
      const rejected = currentSession();
      if (!rejected) throw accountChangedError();
      unauthorizedListeners.forEach((l) => l(rejected));
    }
  }

  if (!res.ok) {
    let message = res.statusText || `Request failed (${res.status})`;
    try {
      const text = await res.text();
      if (text && text.length < 300 && !text.trimStart().startsWith('<')) message = text;
    } catch {
      /* ignore */
    }
    if (res.status === 401) message = 'Your session has expired. Please sign in again.';
    throw new AbsError(message, res.status);
  }

  if (opts.raw) return res as unknown as T;
  const data = await parseBody<T>(res);
  if (accountSignal?.aborted) throw accountChangedError();
  return data;
}

async function parseBody<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const type = res.headers.get('content-type') ?? '';
  if (type.includes('application/json')) return (await res.json()) as T;
  const text = await res.text();
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

/**
 * Builds an absolute media URL (audio, cover) that carries the token as a
 * query parameter. `<audio>` and `<img>` cannot send an Authorization header,
 * so this is how the official ABS web client authorises media too.
 */
export function mediaUrl(path: string, query: Record<string, string | number | undefined> = {}): string {
  const session = getSession();
  if (!session) return '';
  const url = resolveServerPath(session.serverUrl, path);
  // Never hand the token to a host other than the user's server.
  const base = new URL(resolveServerPath(session.serverUrl, '/'), location.href);
  if (new URL(url, location.href).origin !== base.origin) return withQuery(url, query);
  return withQuery(url, { ...query, token: session.accessToken });
}
